/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineComponent, nextTick, Ref, ref, watch } from 'vue';
import {
  useUIStore,
  getHtmlProps,
  getHtmlEmits,
  useNamespace,
} from '@ibiz-template/vue3-util';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import Quill from 'quill';
import { Delta } from 'quill/core';
import {
  CoreConst,
  base64ToBlob,
  getAppCookie,
  IChatMessage,
} from '@ibiz-template/core';
import { isBoolean, isObject } from 'lodash-es';
import { HtmlEditorController } from '../html-editor.controller';
import './quill-editor.scss';

/**
 * 移动端HTML编辑框
 * @primary
 * @description  基于Quill深度定制可扩展的富文本编辑器，集成文件/图片上传、实时预览及样式配置功能，提供完整的富媒体编辑解决方案。支持编辑器类型包含：`移动端HTML编辑框`
 * @editorparams {name:uploadparams,parameterType:string,description:上传参数，图片或文件上传时，用于计算上传路径}
 * @editorparams {name:exportparams,parameterType:string,description:下载参数，图片或文件下载时，用于计算下载路径}
 * @editorparams {name:osscat,parameterType:string,description:用于计算上传和下载路径的OSS参数}
 * @editorparams {name:showtoolbar,parameterType:boolean,defaultvalue:true,description:显示工具栏}
 * @editorparams {name:valuemode,parameterType:'text' | 'html',defaultvalue:html,description:值模式，暂时只支持html模式}
 * @editorparams {name:imagemode,parameterType:'base64' | 'file',defaultvalue:'file',description:图片模式}
 * @editorparams {name:modules,parameterType:string,description:quill配置}
 * @editorparams {name:defaultheight,parameterType:number,defaultvalue:200,description:收缩时的高度}
 * @editorparams {name:showcollapse,parameterType:boolean,defaultvalue:true,description:是否显示伸缩按钮}
 * @editorparams {name:readonly,parameterType:boolean,defaultvalue:false,description:设置编辑器是否为只读态}
 * @editorparams {"name":"srfaiappendcurdata","parameterType":"boolean","defaultvalue":false,"description":"在打开AI功能时，该参数用于判断是否传入对象参数，主要用于在请求历史记录时，附加当前参数"}
 * @editorparams {"name":"srfaiappendcurcontent","parameterType":"string","description":"在打开AI功能时，如果该参数存在值，会将其传入编辑内容作为用户消息，主要用于在请求历史记录后，附加当前编辑内容作为用户消息"}
 * @editorparams {"name":"autoquestion","parameterType":"boolean","defaultvalue":true,"description": "在打开AI功能时历史数据最后一个项是用户消息（USER）时是否自动提问，当打开AI行内聊天时是否自动提问"}
 * @editorparams {"name":"autofill","parameterType":"boolean","defaultvalue":false,"description": "用于AI聊天，AI回答完成之后是否触发回填"}
 * @editorparams {"name":"autoclose","parameterType":"{mode:'minimize' | 'close' | 'closetime',duration?:number}","description": "用于AI聊天，在提问完成后，设置AI窗口的自动关闭模式。其中 mode 设为 minimize 时窗口会最小化，设为 close 时窗口会直接关闭，设为 closetime 时窗口会根据 duration 配置的值延时关闭。duration配置单位为秒（s），默认值为 3 秒"}
 * @editorparams {"name":"enablenoaccess","parameterType":"boolean","defaultvalue":"false", "description":"是否启用无权限模式，若启用无权限模式，上传文件夹需拼接'$'字符，也不需要计算下载凭证"}
 * @editorparams {"name":"globaldownloadprifix","parameterType":"boolean","defaultvalue":"false", "description":"是否使用全局文件下载前缀，若启用，则以global作为前缀"}
 * @editorparams {"name":"srfaiappendresource","parameterType":"string", "description":"AI聊天默认附加资源数据"}
 * @editorparams {"name":"srfmode","parameterType":"string", "description":"指定AI聊天自定义模式"}
 * @editorparams {"name":"srfaichunkview","parameterType":"string", "description":"知识切片视图，用于定义AI交谈打开目标知识切片视图"}
 * @editorparams {"name":"srfaichunkentity","parameterType":"string", "description":"知识切片实体，用于定义AI交谈打开知识切片视图数据主键key"}
 * @editorparams {"name":"chunkview","parameterType":"string", "description":"切片视图，用于定义打开目标切片视图"}
 * @editorparams {"name":"chunkentity","parameterType":"string", "description":"切片实体，用于打开切片视图数据主键key"}
 * @editorparams {"name":"openmode",parameterType:'default' | 'top' | 'bottom',description: 用于AI聊天，AI窗口的打开模式，default：默认窗口模式；top：顶部飘窗模式；bottom：底部飘窗模式}
 * @editorparams {"name":"containerheight",parameterType:"number",description: 用于AI聊天飘窗模式时的高度，0到100按照百分比，大于100按照像素处理}
 * @editorparams {"name":"srfaichatcaption","parameterType":"string", "description":"自定义AI交谈框标题"}
 * @ignoreprops  autoFocus | overflowMode
 * @ignoreemits  blur | focus | infoTextChange | enter
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IBizQuill: any = defineComponent({
  name: 'IBizQuill',
  props: getHtmlProps<HtmlEditorController>(),
  emits: getHtmlEmits(),
  setup(props, { emit }) {
    const ns = useNamespace('quill');
    const c: HtmlEditorController = props.controller;

    const editorRef = ref(null);

    const toolbarRef = ref(null);

    const lang = ibiz.i18n.getLang();

    // 请求头
    const headers: Ref<IData> = ref({
      [`${ibiz.env.tokenHeader}Authorization`]: `${
        ibiz.env.tokenPrefix
      }Bearer ${getAppCookie(CoreConst.TOKEN)}`,
    });

    // 上传文件路径
    const uploadUrl: Ref<string> = ref('');

    // 下载文件路径
    const downloadUrl: Ref<string> = ref('');

    // 编辑状态
    const editing: Ref<boolean> = ref(false);

    // 更新中
    const updating: Ref<boolean> = ref(false);

    // 临时数据
    const tempValue: Ref<string> = ref('');

    // 是否完全展开
    const isCollapse: Ref<boolean> = ref(true);

    const getImage = (delta: Delta) => {
      const item = delta.ops.find(x => x.insert && (x.insert as IData).image);
      if (item) {
        return (item.insert as IData).image;
      }
    };

    const getValue = () => {
      if (c.valueMode === 'text') {
        return c.quill!.getText();
      }
      return c.quill!.getSemanticHTML();
    };

    // 处理图片上传(文件模式)
    const handleUpload = async (image: string) => {
      const blob = base64ToBlob(image);
      const file = await ibiz.util.file.fileUpload(
        uploadUrl.value,
        blob,
        headers.value,
      );
      let url = downloadUrl.value.replace('%fileId%', file.fileid);
      if (ibiz.config.common.enableDownloadTicket && !c.enableNoAccess) {
        const downloadTicket = await ibiz.util.file.getDownloadTicket(
          c.context,
          c.params,
          props.data || {},
          { fileId: file.id },
          c.downloadTicketParams,
        );
        if (downloadTicket && downloadTicket.ticket)
          url = downloadUrl.value.replace('%fileId%', downloadTicket.ticket);
      }
      const value = getValue();
      tempValue.value = value.replace(image, url);
    };

    const init = () => {
      if (!editorRef.value) {
        return;
      }
      const theme = c.showToolbar ? 'snow' : 'bubble';
      c.quill = new Quill(editorRef.value, {
        theme,
        modules: {
          toolbar: toolbarRef.value,
        },
        readOnly: props.disabled || props.readonly,
        placeholder: c.placeHolder,
      });
      c.quill.on('text-change', (delta, oldDelta, source) => {
        if (source === 'user') {
          const image = getImage(delta);
          if (image && c.imageMode === 'file') {
            handleUpload(image);
            return;
          }
          tempValue.value = getValue();
        }
      });
      if (c.valueMode === 'text') {
        c.quill.setText(props.value || '');
      }
    };

    watch(
      () => [props.disabled, props.readonly],
      () => {
        if (!c.quill) {
          return;
        }
        if (props.disabled || props.readonly) {
          c.quill.enable(false);
        } else {
          c.quill.enable();
        }
      },
      { immediate: true },
    );

    // data响应式变更基础路径
    watch(
      () => props.data,
      newVal => {
        if (newVal) {
          const editorParams: IData = {
            ...c.editorParams,
            enableNoAccess: c.enableNoAccess,
            globalDownloadPrifix: c.globalDownloadPrifix,
          };
          if (editorParams.uploadparams) {
            editorParams.uploadParams = JSON.parse(editorParams.uploadparams);
          }
          if (editorParams.exportparams) {
            editorParams.exportParams = JSON.parse(editorParams.exportparams);
          }
          const urls = ibiz.util.file.calcFileUpDownUrl(
            c.context,
            c.params,
            newVal,
            editorParams,
          );
          uploadUrl.value = urls.uploadUrl;
          downloadUrl.value = urls.downloadUrl;
        }
      },
      { immediate: true, deep: true },
    );

    watch(
      () => props.value,
      () => {
        if (!c.quill || !props.value) {
          return;
        }
        tempValue.value = props.value;
        if (c.valueMode === 'text') {
          c.quill.setText(props.value);
        } else {
          updating.value = true;
          nextTick(() => {
            updating.value = false;
          });
        }
      },
      { immediate: true },
    );

    // AI 聊天实例
    let chatInstance: any;

    /**
     * @description 点击AI
     * @returns {*}
     */
    const handleAIClick = async () => {
      const appDataEntityId = c.model.appDataEntityId;
      if (!appDataEntityId || !c.deACMode) return;
      const { zIndex } = useUIStore();
      const containerZIndex = zIndex.increment();
      chatInstance = await ibiz.aiChatUtil.getAIChat();
      const { containerOptions, chatOptions } =
        await ibiz.aiChatUtil.getEditorExAIChatParams(
          c.editorParams,
          c.context,
          c.params,
          props.data || {},
          c.deACMode,
          { chatInstance, view: c.view, ctrl: c.ctrl },
        );
      const resourceOptions = await ibiz.aiChatUtil.getAIResourceOptions(
        c.context,
        c.params,
      );
      chatInstance.create({
        resourceOptions,
        containerOptions: {
          // vant组件层级为2000+，此处需添加2000
          zIndex: containerZIndex + 2000,
          ...containerOptions,
        },
        chatOptions: {
          context: { ...c.context },
          params: { ...c.params, srfactag: c.deACMode.codeName },
          appDataEntityId,
          ...chatOptions,
          action: (action: string, message: IChatMessage) => {
            if (action === 'backfill') emit('change', message.realcontent);
          },
        },
      });
    };

    // 展开编辑
    const handleEdit = () => {
      if (!c.quill) {
        init();
      }
    };

    // 取消编辑
    const handleCancel = () => {
      editing.value = false;
    };

    // 确认编辑
    const handleConfirm = () => {
      emit('change', tempValue.value);
      editing.value = false;
    };

    const handleCustomAction = async (model: IData) => {
      const data = await c.doCustomUIAction(model.uiactionId, c.model.appId);
      if (data.length > 0) {
        emit('customAction', { tag: model.uiactionId, data });
      }
    };

    // 将工具栏配置转换为dom树
    const getToolbar = () => {
      const result: IData[] = [];
      const { toolbar } = c.modules;
      toolbar.forEach((item: IData[]) => {
        if (Array.isArray(item)) {
          item.forEach(key => {
            result.push(<button class={`ql-${key}`}></button>);
          });
        } else if (isObject(item)) {
          Object.keys(item).forEach(key => {
            const values = item[key] as IData[];
            const options = values.map(value => {
              if (isBoolean(value)) {
                return <option selected></option>;
              }
              return <option value={value}></option>;
            });
            result.push(<select class={`ql-${key}`}>{options}</select>);
          });
        }
      });
      return result;
    };

    // 绘制工具栏
    const renderToolbar = () => {
      const items = getToolbar();

      if (c.model.appDataEntityId && c.deACMode)
        items.unshift(
          <button class={`ql-ai`} onClick={handleAIClick}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 16 16'
              fill='currentColor'
              height='1em'
              width='1em'
              preserveAspectRatio='xMidYMid meet'
              focusable='false'
              class='cherry-menu-AIChart'
            >
              <g
                id='aae1.Base基础/1.icon图标/2.normal/ai-star'
                stroke-width='1'
                fill-rule='evenodd'
              >
                <path
                  d='M5.817 1.53l3.158 8.797h.054v.152l1.443 4.021-1.402.001-1.041-2.982H2.495l-1.03 2.982L0 14.5 4.671 1.533l1.146-.003zm7.86 5.424V14.5h-1.213V6.954h1.212zM5.248 3.549l-2.342 6.778h4.706L5.249 3.55zM13.046 0c.075 0 .147.02.204.071a.318.318 0 01.094.181l.064.273c.097.417.17.727.255.968.084.24.177.4.31.523.134.124.318.218.599.306.281.088.65.166 1.15.265a.358.358 0 01.195.095c.056.057.083.13.083.213a.289.289 0 01-.083.21.362.362 0 01-.197.094c-.528.093-.918.167-1.214.255-.295.088-.485.187-.621.324-.137.138-.23.324-.31.606-.08.283-.145.651-.23 1.147a.329.329 0 01-.093.184.293.293 0 01-.206.075.308.308 0 01-.207-.072.322.322 0 01-.1-.188l-.006-.033c-.085-.486-.149-.845-.228-1.12-.079-.274-.17-.452-.305-.585-.135-.133-.323-.23-.618-.32s-.683-.168-1.21-.273a.353.353 0 01-.2-.096.29.29 0 01-.08-.208c0-.079.023-.153.079-.211a.35.35 0 01.2-.097c.5-.098.869-.176 1.15-.263.282-.087.465-.18.597-.302.132-.12.224-.278.306-.511.082-.236.151-.539.244-.947l.071-.312a.312.312 0 01.102-.183.311.311 0 01.205-.069z'
                  id='aae形状结合'
                ></path>
              </g>
            </svg>
          </button>,
        );

      if (c.extraActions.length > 0) {
        c.extraActions.forEach(item => {
          const tag = item.uiactionId.split('@')[0];
          items.push(
            <button
              class={`ql-${tag}`}
              onClick={() => handleCustomAction(item)}
            >
              <iBizIcon icon={item.sysImage} />
            </button>,
          );
        });
      }
      return <div ref='toolbarRef'>{items}</div>;
    };

    return {
      ns,
      c,
      lang,
      editing,
      updating,
      editorRef,
      toolbarRef,
      isCollapse,
      handleEdit,
      handleCancel,
      handleConfirm,
      renderToolbar,
    };
  },
  render() {
    return (
      <div
        class={[
          this.ns.b(),
          this.disabled ? this.ns.m('disabled') : '',
          this.readonly ? this.ns.m('readonly') : '',
          this.ns.m(this.lang.toLowerCase()),
        ]}
      >
        {!this.updating && (
          <iBizQuillPreview
            value={this.value}
            controller={this.controller}
            disabled={this.disabled}
            readonly={this.readonly}
            showCollapse={this.c.showCollapse}
            defaultHeight={this.c.defaultHeight}
            isCollapse={this.isCollapse}
            onEdit={() => {
              this.editing = true;
            }}
            onCollapse={(val: boolean) => {
              this.isCollapse = val;
            }}
          />
        )}
        <van-action-sheet
          v-model:show={this.editing}
          teleport='body'
          class={[this.ns.e('popup'), this.ns.m(this.lang.toLowerCase())]}
          onOpened={this.handleEdit}
        >
          <div class={this.ns.e('content')}>
            {this.renderToolbar()}
            <div ref='editorRef'>
              {this.controller.valueMode === 'html' ? (
                <div v-html={this.value}></div>
              ) : null}
            </div>
          </div>
          <div class={this.ns.e('footer')}>
            <van-button class={this.ns.e('cancel')} onClick={this.handleCancel}>
              {ibiz.i18n.t('editor.common.cancel')}
            </van-button>
            <van-button
              class={this.ns.e('confirm')}
              onClick={this.handleConfirm}
              type='primary'
            >
              {ibiz.i18n.t('editor.common.confirm')}
            </van-button>
          </div>
        </van-action-sheet>
      </div>
    );
  },
});

export default IBizQuill;
